import {
  validateOrgNumber,
  validatePersonalNumber,
} from '@qred/shared-component-library/src/validators';
import { Market } from '~/sharedInterfaces/SharedEnums';

type CountryCodeRegex = {
  [key in Market]: RegExp;
};

export const normalizeInput = (input: string | number, market: Market): string => {
  const inputStr = String(input);

  // For markets where special characters are part of the required format, don't normalize
  const marketsWithRequiredFormatting: Market[] = [Market.Belgium, Market.Denmark, Market.Finland];

  if (marketsWithRequiredFormatting.includes(market)) {
    return inputStr.trim();
  }

  // For other markets (SE, NO, NL), remove hyphens and spaces to allow flexible input
  return inputStr.replaceAll(/[-\s]/g, '');
};

// Personal number regex patterns aligned with CORE service validation rules
const personalNumberRegex: CountryCodeRegex = {
  be: /\d{2}\.\d{2}\.\d{2}-\d{3}\.\d{2}/,
  se: /^(19|20)\d{10}$/,
  no: /^\d{11}$/,
  dk: /^\d{6}-\d{4}$/,
  fi: /^\d{6}[-A]\d{3}[0-9A-Y]$/,
  nl: /.*/, // Match anything for Netherlands as personal number is not used.
  // de: /.*/, // Match anything for Germany until requirements are clear.
};

export const isValidPersonalNumber = (
  personalNumber: string | number,
  market?: string
): boolean => {
  if (!market) {
    return false;
  }

  const marketKey = market.toLowerCase() as Market;
  const regex = personalNumberRegex[marketKey];
  if (!regex) {
    return false;
  }

  const normalizedPersonalNumber = normalizeInput(personalNumber, marketKey);

  return (
    regex.test(normalizedPersonalNumber) && validatePersonalNumber(normalizedPersonalNumber, market)
  );
};

const companyNumberRegex: CountryCodeRegex = {
  be: /^[0]?[0-9]{10}$/,
  se: /^[0-9]{10}$/,
  no: /^[0-9]{9}$/,
  dk: /^[0-9]{8}$/,
  fi: /^[0-9]{7}-[0-9]$/,
  nl: /^[0-9]{8}$/,
  // de: /^(HRA|HRB|VR) (\d{3,6})( [A-Z]{1,2})?$|^\d{10,12}$/,
};

export const isValidCompanyNumber = (companyNumber: string | number, market?: string): boolean => {
  if (!market) {
    return false;
  }

  const marketKey = market.toLowerCase() as Market;
  const regex = companyNumberRegex[marketKey];
  if (!regex) {
    return false;
  }

  const normalizedCompanyNumber = normalizeInput(companyNumber, marketKey);

  return regex.test(normalizedCompanyNumber) && validateOrgNumber(normalizedCompanyNumber, market);
};
